import React from 'react';

/**
 * Skeletons de F4.3b — la silueta honesta de cada pantalla mientras llega el
 * dato (regla de Carlos: pantalla correcta al instante, o skeleton; nunca un
 * spinner de navegación, nunca dato viejo que brinca).
 *
 * UNA base (SkeletonBlock, shimmer sutil vía animate-pulse — el estándar del
 * repo) y variantes por pantalla que COMPONEN la base imitando la estructura
 * real: cards del panel, filas de lista, tiles+gráfica de analíticas. Las
 * variantes no copian estilos entre sí — si la base cambia, cambian todas.
 */

export function SkeletonBlock({ className = '' }) {
  return <div aria-hidden="true" className={`animate-pulse rounded-md bg-white/[0.06] ${className}`} />;
}

/* Fila de lista genérica (doc de biblioteca, sesión del historial, grupo pendiente).
 *
 * H2 (#423) — LA VARIANTE ALTA, Y POR QUÉ 60.5 PX.
 *
 * `variante="tema"` es la fila de «Elige temas», que NO mide lo mismo: la fila real
 * de un tema apila DOS renglones de texto dentro del mismo `p-3` — el nombre
 * (`text-sm`, 20 px de renglon) y el conteo (`text-[11px]`, que hereda el 1.5 del
 * preflight: 16.5) —, o sea 12 + 20 + 16.5 + 12 = 60.5. Con seis filas de 60.5 y
 * cinco huecos de 4, la silueta de la espera llena EXACTAMENTE los 383 px del visor
 * de alto fijo que #420 le puso a esa ventana (6 x 60.5 + 5 x 4 = 383), y la lista
 * de verdad entra sin que nada salte.
 *
 * El alto lo da una caja de 36.5 px con las dos barritas centradas dentro, no las
 * barritas mismas: así se pueden dejar del grueso que se lee bien (16 y 12, con su
 * aire) sin que la suma se mueva ni medio píxel.
 *
 * EL VALOR POR OMISIÓN NO CAMBIA: el Panel y el Centro de Estudio siguen pintando
 * sus cuatro barritas de 44 px, byte por byte como antes de esta firma.
 * CADUCA (remate de pantallas-alumno, R5): ni el Panel ni el Centro de Estudio usan ya
 * estos esqueletos (el del Centro, el de Analíticas y el del detalle se quitaron sin uso);
 * la fila genérica se queda sin sitio de llamada y la de temas sigue siendo la única.
 *
 * #461 (app-3) -- Y AHORA MIDE 80.5, LA FILA DE UN NOMBRE A DOS RENGLONES. El visor
 * de "Elige temas" se dimensiona para ese caso (ALTO_FILA_2 en TopicPickerModal.js),
 * asi que la barra tambien: la caja de dentro pasa de 36.5 a 56.5 px (12 + 56.5 + 12).
 * Es la MISMA variante, no una nueva: su unico sitio de llamada es la ventana de
 * temas, y una variante nueva habria dejado esta sin nadie que la pidiera. */
function SkeletonRow({ variante = 'normal' }) {
  if (variante === 'tema') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-[#0e0e0e]">
        <SkeletonBlock className="w-5 h-5 rounded-full shrink-0" />
        <div className="flex-1 min-w-0 h-[56.5px] flex flex-col justify-center gap-1.5">
          <SkeletonBlock className="h-4 max-w-[70%]" />
          <SkeletonBlock className="h-3 w-16" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-[#0e0e0e]">
      <SkeletonBlock className="w-5 h-5 rounded-md shrink-0" />
      <SkeletonBlock className="h-4 flex-1 max-w-[60%]" />
      <SkeletonBlock className="h-3 w-12 shrink-0" />
    </div>
  );
}

/**
 * #413 — LA VENTANA DE TEMAS, MIENTRAS LLEGAN LOS TEMAS.
 *
 * Sustituye al círculo girando que vivía dentro del selector. Lo que se arregla
 * no es el spinner en sí: es que en el teléfono la hoja tarda 500 ms en entrar y
 * durante ese medio segundo la ventana no tiene UNA SOLA FILA — y cuando la
 * lista llega, la hoja cambia de alto de golpe. Con filas del alto correcto la
 * hoja entra ya con cuerpo y no pega el salto.
 *
 * Compone el `SkeletonRow` de este mismo archivo: cero clases nuevas y, si la
 * base cambia, cambia con ella.
 *
 * #420 — SEIS BARRITAS Y SIN MARGEN PROPIO. Eran cuatro y traía su propia
 * separación de arriba. Desde que la ventana de temas tiene un visor de alto
 * fijo, el skeleton se pinta DENTRO de ese visor: el margen ya lo pone el visor
 * (ponerlo aquí también lo duplicaría) y el número de barritas se sube a seis
 * para que la silueta de la espera se parezca a la lista que va a llegar.
 *
 * H2 (#423) — Y AHORA TAMBIÉN DEL ALTO CORRECTO. Las seis medían 44 px, que es el
 * alto de la fila genérica, no el de una fila de tema (60.5): la silueta llenaba
 * 284 de los 383 px del visor y al llegar la lista la mancha crecía de golpe.
 * `variante="tema"` las lleva a 60.5 y la cuenta cierra exacta. El Panel y el
 * Centro de Estudio NO se enteran: siguen con el valor por omisión.
 *
 * #461 (app-3) -- SEIS BARRAS DE 80.5, Y LA SEXTA LA CORTA EL VISOR. Con el visor de
 * 485 caben cinco enteras (5 x 80.5 + 5 x 4 = 422.5) y la sexta asoma 62.5 px, igual
 * que una fila real queda cortada. El alto del visor es el mismo en los cuatro
 * estados, asi que al llegar la lista nada brinca.
 */
export function TopicPickerSkeleton({ rows = 6 }) {
  return (
    <div className="space-y-1" data-testid="topic-picker-skeleton">
      {Array.from({ length: rows }, (_, i) => <SkeletonRow key={i} variante="tema" />)}
    </div>
  );
}

function SkeletonHeading() {
  return (
    <div className="space-y-2">
      <SkeletonBlock className="h-7 w-48" />
      <SkeletonBlock className="h-4 w-72 max-w-full" />
    </div>
  );
}

/** Panel: encabezado + zona de subida + filas de la biblioteca.
 *
 * dashboard-rediseno (I1, 15-oct-2026): LA SILUETA COPIA EL ACOMODO NUEVO. El saludo con Study a
 * la derecha, la tarjeta para agregar material, la fila de "Your documents" con su contador, la
 * fila del grupo a lo ancho y las tarjetas en la MISMA rejilla del Panel (1, 2 o 3 columnas), cada
 * una del alto de la de verdad (la anatomia fija: 174 px, con su hueco del menu de tres puntos).
 * Nada brinca cuando llegan los datos. La usa tambien el arranque web (`ESQUELETOS_DEL_ARRANQUE`
 * de App.js). El degradado de la casa (#181818 a #232323) va en linea en cada bloque. */
const DEGRADADO = { backgroundImage: 'linear-gradient(90deg, #181818, #232323)' };
function BloqueDelPanel({ className = '' }) {
  return <div aria-hidden="true" className={`animate-pulse rounded-md ${className}`} style={DEGRADADO} />;
}
function TarjetaEsqueleto() {
  return (
    <div className="rounded-[18px] border border-[#222222] bg-[#141414] p-4 flex flex-col">
      <div className="flex items-start gap-3 h-10">
        <BloqueDelPanel className="w-10 h-10 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0 space-y-2 pt-0.5">
          <BloqueDelPanel className="h-4 w-4/5" />
          <BloqueDelPanel className="h-4 w-1/2" />
        </div>
        <BloqueDelPanel className="w-6 h-6 rounded-lg shrink-0 mt-2" />
      </div>
      <div className="mt-2.5 h-12 space-y-2 pt-0.5">
        <BloqueDelPanel className="h-3 w-3/5" />
        <BloqueDelPanel className="h-3 w-2/5" />
      </div>
      <div className="h-11 flex items-center gap-3">
        <BloqueDelPanel className="h-2 flex-1 rounded-full" />
        <BloqueDelPanel className="h-10 w-28 rounded-xl" />
      </div>
    </div>
  );
}
export function DashboardSkeleton() {
  return (
    <div className="space-y-[18px] md:space-y-7 w-full max-w-[1200px] mx-auto" data-testid="skeleton-dashboard">
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-2 min-w-0 flex-1">
          <BloqueDelPanel className="h-7 md:h-10 w-48 md:w-72 max-w-full" />
          <BloqueDelPanel className="hidden md:block h-4 w-64" />
        </div>
        <BloqueDelPanel className="h-11 md:h-[52px] w-24 md:w-28 rounded-[14px] shrink-0" />
      </div>
      <div className="rounded-[18px] border border-[#222222] bg-[#141414] p-3.5">
        <div className="flex items-center gap-2 md:gap-3">
          <BloqueDelPanel className="h-[52px] md:h-11 flex-1 rounded-[14px]" />
          <BloqueDelPanel className="h-[52px] w-[52px] md:h-11 md:w-44 rounded-[14px] shrink-0" />
        </div>
        <BloqueDelPanel className="hidden md:block mt-3 h-3 w-56" />
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <BloqueDelPanel className="h-5 w-40" />
          <BloqueDelPanel className="h-4 w-24" />
        </div>
        <div className="grid grid-cols-1 min-[640px]:grid-cols-2 min-[1181px]:grid-cols-3 gap-4">
          <div className="col-span-full rounded-[18px] border border-[#222222] bg-[#141414] p-4 flex items-center gap-3">
            <BloqueDelPanel className="w-11 h-11 rounded-xl shrink-0" />
            <div className="flex-1 space-y-2">
              <BloqueDelPanel className="h-4 w-40" />
              <BloqueDelPanel className="h-3 w-28" />
            </div>
          </div>
          {[0, 1, 2, 3, 4, 5].map((i) => <TarjetaEsqueleto key={i} />)}
        </div>
      </div>
    </div>
  );
}

/** Historial / Pendientes: filas de lista dentro de un contenedor de vidrio. */
export function ListSkeleton({ rows = 5, testid = 'skeleton-list' }) {
  return (
    <div className="space-y-3" data-testid={testid}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="glass-card rounded-[16px] p-4 flex items-center gap-4">
          <SkeletonBlock className="w-9 h-9 rounded-xl shrink-0" />
          <div className="flex-1 space-y-2">
            <SkeletonBlock className="h-4 w-1/2" />
            <SkeletonBlock className="h-3 w-1/3" />
          </div>
          <SkeletonBlock className="h-6 w-14 rounded-full shrink-0" />
        </div>
      ))}
    </div>
  );
}

/** Sección de Settings (suscripción / historial de packs): líneas de texto. */
export function SettingsSectionSkeleton({ testid = 'skeleton-settings-section' }) {
  return (
    <div className="space-y-3 py-1" data-testid={testid}>
      <SkeletonBlock className="h-4 w-2/3" />
      <SkeletonBlock className="h-4 w-1/2" />
      <SkeletonBlock className="h-9 w-40 rounded-xl" />
    </div>
  );
}

/**
 * Fallback de Suspense para chunks lazy de ruta (pantalla completa: durante la
 * carga del chunk no hay layout todavía). Con el prefetch en idle de App.js
 * casi nunca se ve — pero si se ve, es silueta, no spinner.
 */
export function PageSkeleton() {
  return (
    <div className="min-h-screen bg-[#0e0e0e] px-6 py-24 md:px-16" data-testid="skeleton-page">
      <div className="max-w-3xl mx-auto space-y-8">
        <SkeletonHeading />
        <div className="glass-card rounded-[20px] p-6 space-y-3">
          <SkeletonBlock className="h-5 w-3/4" />
          <SkeletonBlock className="h-4 w-full" />
          <SkeletonBlock className="h-4 w-5/6" />
          <SkeletonBlock className="h-11 w-full rounded-xl" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SkeletonBlock className="h-24 rounded-[16px]" />
          <SkeletonBlock className="h-24 rounded-[16px]" />
        </div>
      </div>
    </div>
  );
}
